import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isNativeApp } from '../../utils/capacitorBridge';
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed,
} from '../../services/pushNotificationService';

const DISMISSED_KEY = '9meals_push_prompt_dismissed';
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

/**
 * Bottom-sheet style prompt asking the user to enable push notifications.
 * Shown once the service worker is registered and the user hasn't dismissed recently.
 * Only rendered when `triggerReady` prop is true (e.g., after first meal plan generation).
 */
function NotificationPromptInner({ triggerReady }) {
  const { t } = useTranslation('common');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!triggerReady || !isPushSupported()) return;

    (async () => {
      const perm = await getNotificationPermission();
      if (perm === 'granted') {
        // Already granted — ensure subscription exists silently
        const sub = await isSubscribed();
        if (!sub) await subscribeToPush();
        return;
      }
      if (perm === 'denied') return; // can't ask again

      const dismissed = localStorage.getItem(DISMISSED_KEY);
      if (dismissed && Date.now() - Number(dismissed) < DISMISS_COOLDOWN_MS) return;

      setShow(true);
    })();
  }, [triggerReady]);

  const handleEnable = useCallback(async () => {
    setLoading(true);
    await subscribeToPush();
    setLoading(false);
    setShow(false);
  }, []);

  const handleDismiss = useCallback(() => {
    setShow(false);
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  }, []);

  if (!show) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 z-[9998] mx-auto max-w-md rounded-2xl bg-[#0D0C07] p-4 shadow-xl"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#FFCB0020] text-xl">
          🔔
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">
            {t('pushPrompt.title', 'Stay in the loop')}
          </p>
          <p className="mt-0.5 text-xs text-[#a9a8a3]">
            {t('pushPrompt.description', 'Get notified about your meal plans and new features')}
          </p>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          onClick={handleDismiss}
          className="flex-1 rounded-lg border border-[#333] px-3 py-2 text-sm font-medium text-[#a9a8a3] transition-colors hover:border-[#555]"
        >
          {t('pushPrompt.dismiss', 'Not now')}
        </button>
        <button
          onClick={handleEnable}
          disabled={loading}
          className="flex-1 rounded-lg bg-[#FFCB00] px-3 py-2 text-sm font-semibold text-[#0D0C07] transition-colors hover:bg-[#e6b800] disabled:opacity-50"
        >
          {loading
            ? t('pushPrompt.enabling', 'Enabling…')
            : t('pushPrompt.enable', 'Enable')}
        </button>
      </div>
    </div>
  );
}

// Outer wrapper — early return before inner component so no hooks are called conditionally
function NotificationPrompt({ triggerReady = false }) {
  if (isNativeApp()) return null;
  return <NotificationPromptInner triggerReady={triggerReady} />;
}

export default NotificationPrompt;
